import { useEffect, useSyncExternalStore } from 'react';

export const STORAGE = {
  savedHistory: 'savedHistory',
} as const;

let callbacks: (() => void)[] = [];
const emitChange = () => {
  for (const callback of callbacks) {
    callback();
  }
};
const subscribe = (callback: () => void) => {
  callbacks = [...callbacks, callback];
  window.addEventListener('storage', callback);
  return () => {
    callbacks = callbacks.filter(l => l !== callback);
    window.removeEventListener('storage', callback);
  };
};

declare global {
  interface Window {
    IN_MEMORY_STORAGE: Record<string, unknown>;
  }
}

window.IN_MEMORY_STORAGE ||= {};

export const useInMemoryStorage = <T>(key: string, initialValue: T) => {
  const getSnapshot = () => {
    return window.IN_MEMORY_STORAGE[key] as T;
  };
  const store = useSyncExternalStore(subscribe, getSnapshot);

  const setStore = (value: (T | null) | ((previousValue: T) => T)) => {
    window.IN_MEMORY_STORAGE[key] =
      value instanceof Function ? value(store || initialValue) : value;
    emitChange();
  };
  const shouldInitialize = store === null && initialValue;

  useEffect(() => {
    if (shouldInitialize) {
      window.IN_MEMORY_STORAGE[key] = initialValue;
    }
  }, [shouldInitialize, initialValue, key]);

  return [store ?? initialValue, setStore] as const;
};
